<template>
  <div
    :class="['oap-findation-wrapper', { 'oap-findation-wrapper--standalone': isStandalonePage }]"
  >
    <div id="findation-widget-button" ref="findationButton"></div>
    <LoadScript
      v-if="shouldLoadScript"
      :attributes="{ type: 'text/javascript' }"
      src="https://assets.findation.com/javascripts/w-adv-13.min.js"
      name="findation"
      @loaded="initWidget"
    >
    </LoadScript>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import LoadScript from '@Foundation/loadscript/loadscript.vue';
import { intersectionViewportObserver } from '@Foundation';
import {
  getAnalytics,
  pushWidgetEmbeddedTagging,
  proceedTriggerRedirect,
  pushNoMatchTagging,
  pushAddMoreProductsTagging,
  pushVToTagging,
  pushCloseTagging,
  pushShadeRecommendationTagging
} from '../tagging';

const props = defineProps({
  widgetKey: { type: String, required: true },
  productApiUrl: { type: String, required: true, default: '' },
  locationOrigin: { type: String, default: '' },
  isStandalonePage: { type: Boolean, default: false },
  searchSetId: { type: String, default: '' },
  analytics: { type: Object, default: () => {} },
});

const findationButton = ref(null);
let shouldLoadScript = ref(false);
let productInfo = ref('');
let recommendedProductShade = ref('');

onMounted(() => {
  intersectionViewportObserver(findationButton.value).then(() => {
    shouldLoadScript.value = true;
  });

  productInfo.value = props.isStandalonePage
    ? 'undefined'
    : `${props.analytics?.productName}::${props.analytics?.productId}`;
});

function initWidget() {
  window.Findation = window.Findation || {};

  const searchSetLookupReference = props.searchSetId;

  Findation.init(findationButton.value, props.widgetKey, {
    buttonWidth: '100%',
    buttonHeight: '40px',
    searchSetLookupReference,
  });

  Findation.widgetEmbedded = () => {
    if (props.isStandalonePage) Findation.openWidget();

    pushWidgetEmbeddedTagging();
  };

  Findation.onActionReceived = (payload) => {
    const eventName = payload.event;

    switch (eventName) {
      case 'triggerRedirect':
        const { productApiUrl } = props;
        proceedTriggerRedirect(payload, productApiUrl, productInfo.value);
        break;
      case 'goTo':
        proceedGoToEvent(payload);
        break;
      case 'callBack':
        proceedCallBack(payload);
        break;
    }
  };

  Findation.widgetDidOpen = () => {
    if (!props.analytics) return;

    const ctaName = props.analytics?.mainFindationButton;
    getAnalytics('opened', ctaName, productInfo.value);
  };

  Findation.onSearchComplete = (payload) => {
    if (!props.analytics) return;
    recommendedProductShade.value = payload?.recommendedShade || 'none';
    getAnalytics('shades entered', props.analytics?.submitButton, productInfo.value);

    const { matchSuccess } = payload;
    if (matchSuccess) {
      const ctaName = props.analytics?.submitButton;
      pushShadeRecommendationTagging(ctaName, productInfo.value, recommendedProductShade.value);
    } else {
      const ctaName = props.analytics?.mainFindationButton;
      pushNoMatchTagging(ctaName, productInfo.value);
    }
  };

  Findation.widgetDidClose = () => {
    pushCloseTagging(productInfo.value);
  };
}

function proceedGoToEvent(payload) {
  const { redirectCategory, url } = payload;
  const locationOrigin = props.locationOrigin ? props.locationOrigin : window.location.origin;

  if (!url || !props.analytics) return;
  const ctaName = props.analytics?.tryOurVirtualTryonTool;

  switch (redirectCategory) {
    case 'vto':
      pushVToTagging(ctaName, productInfo.value, recommendedProductShade.value);
      break;
  }

  window.location.assign(`${locationOrigin}${url}`);
}

function proceedCallBack(payload) {
  const { eventCategory } = payload;
  if (!eventCategory || !props.analytics) return;
  const ctaName = props.analytics?.addMoreProductsButton;

  switch (eventCategory) {
    case 'add-more-products':
      pushAddMoreProductsTagging(ctaName, productInfo.value, recommendedProductShade.value);
      break;
  }
}
</script>
